import { create } from 'zustand';
import {
  completedStepNumber,
  findObservedScopedSource,
  isConfirmedDone,
  isConfirmedRemoval,
  type TrellusBulkDialResult,
  type TrellusCapabilities,
  type TrellusCompletionPolicy,
  type TrellusContext,
  type TrellusDialScope,
  type TrellusFocusResult,
  type TrellusGongConnectionState,
  type TrellusScopedSource,
  type TrellusSelectionResult,
  type TrellusTaskEvent,
} from './host-schemas';

/**
 * What Clay currently knows about the Trellus extension in this document.
 *
 * Every field is a UI HINT fed by untrusted page events (`host-events-bridge.ts`
 * is the only writer in production). Nothing here authorizes a backend write:
 * `task_events` and `tombstones` drive LOCAL UI; the one server call is the
 * owner-only dialer-event hint posted by `useTrellusCompletionSync` (gong
 * reconcile U20), which asks the server to re-check a row through reconcile
 * (see docs/flow/trellus-dialer.md for the D8 trust boundary).
 *
 * Zustand per CLAUDE.md "Zustand for Module-Scope Client State": plain
 * `create<State>(() => ({ ...initialState }))`, data-only state, writers as
 * module-level functions (the `flow-panels-store.ts` precedent) so
 * `useTrellusHostStore.setState(initialState, true)` — the test reset — can
 * never strip behavior.
 */

/** Trellus retains the last 100 document events; the buffer matches. */
export const TRELLUS_TASK_EVENT_BUFFER = 100;
/** Cap on remembered completions (oldest dropped first). */
export const TRELLUS_TOMBSTONE_CAP = 2000;

export type TrellusHandshakeStatus = 'idle' | 'pending' | 'available' | 'unavailable';

/**
 * What a tombstone remembers: the event that set it and the Gong step it completed (`null` = every step).
 * `expires_at` (epoch ms) is set only by a dated snooze answer: past it the tombstone no longer hides the row.
 */
export interface TrellusTombstone {
  eventId: string;
  step: number | null;
  expires_at?: number;
}

/** Cap on latched scoped sources per document (oldest dropped first). */
export const TRELLUS_SCOPED_LATCH_CAP = 50;

/** Every scoped source observed so far for ONE document nonce; see `TrellusHostState.scoped_latch`. */
export interface TrellusScopedLatch {
  nonce: string;
  sources: readonly TrellusScopedSource[];
}

export interface TrellusHostState {
  handshake: TrellusHandshakeStatus;
  /** True once the extension answered `available:true`; false again if its document identity goes stale. */
  extension_present: boolean;
  build_id: string | null;
  nonce: string | null;
  /** Advertised host commands (e.g. `sidebar.toggle`). Gate every command on this. */
  commands: readonly string[];
  outbound_events: readonly string[];
  /** Advertised queue sources (`signals`, `contacts`, `contacts_scoped`, …); `[]` on a build that lists none. Gate on `hasTrellusSource`. */
  sources: readonly string[];
  /** Advertised component ids (`bulk_dial`, `dial_button`, `gong_connection`, `sidebar`, `launcher`). */
  components: readonly string[];
  /** Capabilities advertised the test barrier (automated QA only). */
  is_test_barrier: boolean;
  context: TrellusContext | null;
  last_selection_result: Pick<TrellusSelectionResult, 'accepted' | 'reason_code' | 'sequence'> | null;
  last_focus_result: Pick<TrellusFocusResult, 'focused' | 'reason_code'> | null;
  /** Detailed Gong connection state from `GONG_CONNECTION_STATE` (open string: `checking`, `sign_in_required`, ...). */
  gong_state: string | null;
  gong_ready: boolean | null;
  /** Outcome of the rep's last bulk-dial press, verbatim: `{status, reason_code, counts}` (+ mode/kind/flags). */
  last_bulk_dial_result: TrellusBulkDialResult | null;
  completion_policy: TrellusCompletionPolicy | null;
  /** Latest `config_revision` per component, from `UI_CONFIGURATION_CHANGED`. */
  ui_config_revisions: Readonly<Record<string, number>>;
  /** Recent task events by `event_id`; `task_event_ids` is their arrival order (bounded). */
  task_events: Readonly<Record<string, TrellusTaskEvent>>;
  task_event_ids: readonly string[];
  /**
   * Local completed tombstones: `signal:<clay_signal_id>` -> the event that set it
   * and the step it completed (`null` for a removal or a completion with no step). A later confirmed event on
   * the same key REPLACES the entry. Written only by non-test, non-view-as `task_completed` /
   * `task_already_completed` / `flow_removed` events. The Signals table hides tombstoned rows
   * (`useSignalTableModel`, via `isTombstonedAtStep`); never sent anywhere.
   */
  tombstones: Readonly<Record<string, TrellusTombstone>>;
  /**
   * Latch of the scoped sources the extension has OBSERVED for the current document nonce. Once a scoped list was seen,
   * the dial segment stays available for it for the rest of that nonce even if a later context reports an empty
   * `observed_sources` (the segment must never unmount mid-session: that would abort its own publish). Resets on a new
   * nonce and on a stale document identity; availability is still checked per scope, so another account never inherits it.
   */
  scoped_latch: TrellusScopedLatch | null;
}

export const initialState: TrellusHostState = {
  handshake: 'idle',
  extension_present: false,
  build_id: null,
  nonce: null,
  commands: [],
  outbound_events: [],
  sources: [],
  components: [],
  is_test_barrier: false,
  context: null,
  last_selection_result: null,
  last_focus_result: null,
  gong_state: null,
  gong_ready: null,
  last_bulk_dial_result: null,
  completion_policy: null,
  ui_config_revisions: {},
  task_events: {},
  task_event_ids: [],
  tombstones: {},
  scoped_latch: null,
};

export const useTrellusHostStore = create<TrellusHostState>(() => ({ ...initialState }));

const set = useTrellusHostStore.setState;

export function setTrellusHandshake(handshake: TrellusHandshakeStatus): void {
  set({ handshake });
}

/** Apply a capability handshake result; `null` = the document identity went stale (extension reloaded / disabled). */
export function applyTrellusCapabilities(capabilities: TrellusCapabilities | null): void {
  if (!capabilities) {
    set({
      handshake: 'idle',
      extension_present: false,
      nonce: null,
      commands: [],
      outbound_events: [],
      sources: [],
      components: [],
      is_test_barrier: false,
      context: null,
      scoped_latch: null,
    });
    return;
  }
  const latch = useTrellusHostStore.getState().scoped_latch;
  set({
    ...(latch !== null && latch.nonce !== capabilities.nonce ? { scoped_latch: null } : {}),
    handshake: 'available',
    extension_present: true,
    build_id: capabilities.build_id ?? null,
    nonce: capabilities.nonce,
    commands: capabilities.commands,
    outbound_events: capabilities.outbound_events,
    sources: capabilities.sources,
    components: Object.keys(capabilities.components),
    is_test_barrier: capabilities.limits?.is_test_barrier === true,
    ...(capabilities.completion ? { completion_policy: capabilities.completion } : {}),
  });
}

export function applyTrellusContext(context: TrellusContext): void {
  const prev = useTrellusHostStore.getState().context;
  // A context that names an active list (`kind` set) and OMITS `observed_sources` (the reply to a scoped request describes
  // one list) says nothing about what else is observed: keep the last report for this document, or the dial it just opened
  // would close under its own publish. An EXPLICIT `[]` is the extension saying "nothing observed" and is honoured.
  const keepObserved = prev !== null && prev.nonce === context.nonce && context.kind !== null && context.observed_sources === undefined;
  const effective = keepObserved ? { ...context, observed_sources: prev.observed_sources } : context;
  set({ context: effective, scoped_latch: latchScopedSources(useTrellusHostStore.getState().scoped_latch, effective) });
}

/** Fold the context's observed scoped sources into the per-nonce latch (a new nonce starts a fresh one). */
function latchScopedSources(prev: TrellusScopedLatch | null, context: TrellusContext): TrellusScopedLatch | null {
  const base: readonly TrellusScopedSource[] = prev !== null && prev.nonce === context.nonce ? prev.sources : [];
  const next = [...base];
  for (const entry of context.observed_sources ?? []) {
    const at = next.findIndex(src => src.source_url === entry.source_url && src.kind === entry.kind);
    // Once a usable report is latched for this nonce, a later "unavailable" report of the same source (kept only in
    // `context`) must not overwrite it: the dial would unmount under an in-flight publish.
    if (at >= 0) { if (!(entry.available === false && next[at]!.available !== false)) next[at] = entry; }
    else next.push(entry);
  }
  if (next.length === 0) return prev !== null && prev.nonce === context.nonce ? prev : null;
  return { nonce: context.nonce, sources: next.slice(-TRELLUS_SCOPED_LATCH_CAP) };
}

export function applyTrellusSelectionResult(result: TrellusSelectionResult): void {
  set({ last_selection_result: { accepted: result.accepted, reason_code: result.reason_code ?? null, sequence: result.sequence } });
}

export function applyTrellusFocusResult(result: TrellusFocusResult): void {
  set({ last_focus_result: { focused: result.focused, reason_code: result.reason_code ?? null } });
}

export function applyTrellusGongConnectionState(state: TrellusGongConnectionState): void {
  set({ gong_state: state.state, gong_ready: state.ready });
}

export function applyTrellusBulkDialResult(result: TrellusBulkDialResult): void {
  set({ last_bulk_dial_result: result });
}

/** The rep dismissed (or a later selection made stale) the last bulk-dial outcome. */
export function clearTrellusBulkDialResult(): void {
  set({ last_bulk_dial_result: null });
}

export function applyTrellusCompletionPolicy(policy: Pick<TrellusCompletionPolicy, 'revision' | 'after_call'>): void {
  // The change event carries only the requested preference; keep the richer effective fields from capabilities when revisions agree.
  const prev = useTrellusHostStore.getState().completion_policy;
  if (prev && policy.revision < prev.revision) return;
  set({ completion_policy: { ...policy, requested_after_call: policy.after_call } });
}

export function applyTrellusUiConfigurationChanged(componentId: string, configRevision: number): void {
  const prev = useTrellusHostStore.getState().ui_config_revisions;
  if ((prev[componentId] ?? 0) >= configRevision) return;
  set({ ui_config_revisions: { ...prev, [componentId]: configRevision } });
}

/** Parse-and-store only. Records the event (bounded) and, for a confirmed real completion, a local tombstone. */
export function applyTrellusTaskEvent(event: TrellusTaskEvent): void {
  const state = useTrellusHostStore.getState();
  if (state.task_events[event.event_id]) return; // idempotent on event_id (replay re-emits originals)

  const ids = [...state.task_event_ids, event.event_id];
  const events = { ...state.task_events, [event.event_id]: event };
  while (ids.length > TRELLUS_TASK_EVENT_BUFFER) {
    const evicted = ids.shift();
    if (evicted !== undefined) delete events[evicted];
  }

  let tombstones = state.tombstones;
  // Only a CONFIRMED completion or removal tombstones (same predicate the sync hook posts on);
  // a test event, a View As event, or a pending/failed completion never does.
  if (isConfirmedDone(event)) {
    const next: Record<string, TrellusTombstone> = { ...tombstones };
    const entry: TrellusTombstone = { eventId: event.event_id, step: isConfirmedRemoval(event) ? null : completedStepNumber(event) };
    if (event.clay_signal_id) next[`signal:${event.clay_signal_id}`] = entry;
    const keys = Object.keys(next);
    for (const key of keys.slice(0, Math.max(0, keys.length - TRELLUS_TOMBSTONE_CAP))) delete next[key];
    tombstones = next;
  }
  set({ task_events: events, task_event_ids: ids, tombstones });
}

/** Full reset (client disposed). Equivalent to the test reset. */
export function resetTrellusHost(): void {
  set(initialState, true);
}

// ── Selectors ──────────────────────────────────────────────────────────────

export function hasTrellusCommand(state: Pick<TrellusHostState, 'commands'>, command: string): boolean {
  return state.commands.includes(command);
}

/** Does the extension advertise this queue source (e.g. `contacts_scoped`)? Never infer it from the build or manifest version. */
export function hasTrellusSource(state: Pick<TrellusHostState, 'sources'>, source: string): boolean {
  return state.sources.includes(source);
}

/** The list the extension has OBSERVED for `scope` (its context's `observed_sources`), or null. Null while no context has arrived. */
export function selectObservedScopedSource(state: Pick<TrellusHostState, 'context'>, scope: TrellusDialScope): TrellusScopedSource | null {
  return state.context ? findObservedScopedSource(state.context, scope) : null;
}

/**
 * Is a scoped dial available right now? No `requires` means nothing to wait for. Otherwise the extension must be present
 * AND either advertise the source (`contacts_scoped`) OR have OBSERVED (now, or earlier in this nonce: the latch) a list for this exact `scope`: a build that never
 * advertises the capability still reports what it sees, and that is what a publish is checked against.
 */
export function useTrellusScopedDialAvailable(requires: 'contacts_scoped' | undefined, scope?: TrellusDialScope): boolean {
  return useTrellusHostStore(s => !requires
    || (s.extension_present && (hasTrellusSource(s, requires) || (scope !== undefined && (selectObservedScopedSource(s, scope) !== null || selectLatchedScopedSource(s, scope) !== null)))));
}

/** A scoped source observed for `scope` earlier in THIS document's nonce (survives a later empty `observed_sources`), or null. */
export function selectLatchedScopedSource(state: Pick<TrellusHostState, 'scoped_latch'>, scope: TrellusDialScope): TrellusScopedSource | null {
  return state.scoped_latch ? findObservedScopedSource({ observed_sources: [...state.scoped_latch.sources] }, scope) : null;
}

/**
 * Is this row hidden by a tombstone? Hidden iff an entry exists for
 * `signal:<id>`, it has not expired (`expires_at` <= `now`), AND (the entry has no step, OR the
 * row has no step, OR the row's step is at or before the completed one). A row
 * whose step moved past it is real work again. Pure function of (state, row, now):
 * nothing per-mount, so it survives a remount.
 */
export function isTombstonedAtStep(
  state: Pick<TrellusHostState, 'tombstones'>,
  { signalId, stepNumber }: { signalId: string; stepNumber: number | null },
  now: number = Date.now(),
): boolean {
  const entry = state.tombstones[`signal:${signalId}`];
  if (entry === undefined) return false;
  if (entry.expires_at !== undefined && now >= entry.expires_at) return false;
  return entry.step === null || stepNumber === null || stepNumber <= entry.step;
}

/** The earliest dated-snooze expiry strictly after `now` (epoch ms), or null when none is pending. Pure. */
export function nextTombstoneExpiry(tombstones: Readonly<Record<string, TrellusTombstone>>, now: number): number | null {
  let next: number | null = null;
  for (const entry of Object.values(tombstones)) {
    const at = entry.expires_at;
    if (at !== undefined && at > now && (next === null || at < next)) next = at;
  }
  return next;
}

/**
 * A dated snooze: the server hides the row until `expiresAtMs`, so the local tombstone stops hiding it then.
 * Only the tombstone set by `eventId` is touched: a newer event on the same signal replaced it and stays.
 */
export function expireTrellusTombstone(signalId: string, eventId: string, expiresAtMs: number): void {
  const { tombstones } = useTrellusHostStore.getState();
  const key = `signal:${signalId}`;
  const entry = tombstones[key];
  if (entry?.eventId !== eventId) return;
  set({ tombstones: { ...tombstones, [key]: { ...entry, expires_at: expiresAtMs } } });
}
